import React, { useEffect, useState, useRef } from 'react';
import { useServerStore } from '../../store/serverStore';
import { useAuthStore } from '../../store/authStore';
import { Mic, MicOff, Headphones, PhoneOff, Volume2, Settings, Monitor } from 'lucide-react';
import { getRealtimeSocket } from '../../lib/realtime';
import { useVoiceStore } from '../../store/voiceStore';
import { playJoinSelf, playMute, playUnmute } from '../../lib/sounds';
import VideoStreamViewer from './VideoStreamViewer';

const VoiceChannel: React.FC = () => {
  const { currentChannel } = useServerStore();
  const { user, profile } = useAuthStore();
  const {
    isConnected,
    connectedUsers,
    isMuted,
    localStream,
    isScreenSharing,
    channelVideoTracks,
    joinVoiceChannel,
    leaveVoiceChannel,
    toggleMute,
    toggleScreenShare,
    userSpeakingStates,
  } = useVoiceStore();
  const [audioLevel, setAudioLevel] = useState<number>(0);
  const [watchingStream, setWatchingStream] = useState<{
    stream: MediaStream;
    source: 'camera' | 'screen';
    username: string;
  } | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const audioAnalyserRef = useRef<AnalyserNode | null>(null);

  // Cancel any running animation frame on unmount — do NOT disconnect from voice
  // (the VoiceBar handles persistent connection across navigation)
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, []);

  // When the user is already connected and navigates back to this page,
  // re-attach the local analyser to the existing stream so the indicator works.
  useEffect(() => {
    if (isConnected && localStream) {
      startLocalAnalyser(localStream);
    }
    // Cleanup when disconnecting
    if (!isConnected) {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      audioAnalyserRef.current = null;
      setAudioLevel(0);
    }
    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, localStream]);

  // Local audio level analyser loop — started imperatively after joining
  const startLocalAnalyser = (stream: MediaStream) => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);

    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    audioAnalyserRef.current = analyser;

    const tick = () => {
      if (!audioAnalyserRef.current) return;
      const data = new Uint8Array(audioAnalyserRef.current.frequencyBinCount);
      audioAnalyserRef.current.getByteFrequencyData(data);
      const avg = data.reduce((a, b) => a + b, 0) / data.length;
      setAudioLevel(Math.min(avg / 128, 1));
      animationFrameRef.current = requestAnimationFrame(tick);
    };
    animationFrameRef.current = requestAnimationFrame(tick);
  };

  const handleJoinVoice = async () => {
    if (currentChannel) {
      try {
        const stream = await joinVoiceChannel(currentChannel.id);
        if (stream) {
          playJoinSelf();
          startLocalAnalyser(stream);
        }
      } catch (error) {
        console.error('Error joining voice channel:', error);
      }
    }
  };

  const handleLeaveVoice = () => {
    leaveVoiceChannel();
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    audioAnalyserRef.current = null;
    setAudioLevel(0);
  };

  const handleToggleMute = () => {
    if (isMuted) playUnmute(); else playMute();
    toggleMute();
  };

  const handleToggleScreenShare = () => {
    void toggleScreenShare();
  };

  if (!currentChannel || currentChannel.type !== 'voice') {
    return (
      <div className="flex-1 flex items-center justify-center bg-bg-primary text-text-secondary">
        Select a voice channel to start a call
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-bg-primary">
      <div className="p-4 border-b border-border-color">
        <h2 className="font-bold text-lg flex items-center">
          <Volume2 className="h-5 w-5 mr-2" />
          {currentChannel.name}
        </h2>
        {currentChannel.description && (
          <p className="text-sm text-text-secondary mt-1">{currentChannel.description}</p>
        )}
      </div>
      
      <div className="flex-1 p-4">
        <div className="bg-bg-secondary rounded-lg p-4 h-full flex flex-col">
          {isConnected ? (
            <>
              <div className="flex-1 overflow-y-auto">
                <h3 className="text-sm font-semibold text-text-secondary uppercase mb-2">
                  Connected Users ({connectedUsers.length + 1})
                </h3>
                
                <div className="space-y-2">
                  {/* Current user */}
                  <div className="flex items-center p-2 rounded-md bg-bg-tertiary">
                    <div className="avatar w-10 h-10 mr-3 flex-shrink-0">
                      {profile?.avatar_url ? (
                        <img 
                          src={profile.avatar_url} 
                          alt={profile?.username} 
                          className="w-10 h-10 object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-accent-color text-white flex items-center justify-center rounded-full">
                          {profile?.username?.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-text-primary">{profile?.username} (You)</div>
                      <div className="text-xs text-text-secondary">
                        {isMuted ? 'Muted' : 'Speaking'}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {!isMuted && (
                        <div className="relative w-16 h-2 bg-bg-primary rounded-full overflow-hidden">
                          <div 
                            className="absolute top-0 left-0 h-full bg-accent-color transition-all duration-100"
                            style={{ width: `${audioLevel * 100}%` }}
                          ></div>
                        </div>
                      )}
                      <div className={`w-2 h-2 rounded-full ${isMuted ? 'bg-error' : 'bg-success'}`}></div>
                    </div>
                  </div>
                  
                  {/* Other connected users */}
                  {connectedUsers.map(user => {
                    const videoTrack = channelVideoTracks[user.id];
                    return (
                    <div key={user.id} className="flex items-center p-2 rounded-md bg-bg-tertiary">
                      <div className="avatar w-10 h-10 mr-3 flex-shrink-0">
                        {user.avatar_url ? (
                          <img 
                            src={user.avatar_url} 
                            alt={user.username} 
                            className="w-10 h-10 object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-accent-color text-white flex items-center justify-center rounded-full">
                            {user.username.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-text-primary">{user.username}</div>
                        <div className="text-xs text-text-secondary">
                          {user.muted ? 'Muted' : 'Speaking'}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {!user.muted && (userSpeakingStates[user.id] ?? 0) > 0 && (
                          <div className="relative w-16 h-2 bg-bg-primary rounded-full overflow-hidden">
                            <div 
                              className="absolute top-0 left-0 h-full bg-accent-color transition-all duration-100"
                              style={{ width: `${userSpeakingStates[user.id] * 100}%` }}
                            ></div>
                          </div>
                        )}
                        {videoTrack && (
                          <button
                            onClick={() => setWatchingStream({ ...videoTrack, username: user.username })}
                            className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-error text-white hover:bg-error/80 transition-colors"
                            title="Regarder le stream"
                          >
                            <Monitor className="w-2.5 h-2.5" />
                            LIVE
                          </button>
                        )}
                        <div className={`w-2 h-2 rounded-full ${user.muted ? 'bg-error' : userSpeakingStates[user.id] ? 'bg-success' : 'bg-text-muted'}`}></div>
                      </div>
                    </div>
                    );
                  })}
                </div>
                
                {connectedUsers.length === 0 && (
                  <div className="text-center py-4 text-text-secondary">
                    <p>You're the only one here right now.</p>
                    <p className="text-sm mt-1">Invite others to join this voice channel!</p>
                  </div>
                )}
              </div>
              
              <div className="border-t border-border-color pt-4 mt-4 flex justify-center space-x-4">
                <button
                  className={`p-3 rounded-full transition-colors ${isMuted ? 'bg-error hover:bg-error/90' : 'bg-highlight hover:bg-highlight-hover'}`}
                  onClick={handleToggleMute}
                  title={isMuted ? "Unmute" : "Mute"}
                >
                  {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                </button>
                <button
                  className={`p-3 rounded-full transition-colors ${isScreenSharing ? 'bg-success hover:bg-success/90 text-white' : 'bg-highlight hover:bg-highlight-hover'}`}
                  onClick={handleToggleScreenShare}
                  title={isScreenSharing ? "Arrêter le partage d'écran" : "Partager l'écran"}
                >
                  <Monitor className="h-5 w-5" />
                </button>
                <button
                  className="p-3 rounded-full bg-error hover:bg-error/90 transition-colors"
                  onClick={handleLeaveVoice}
                  title="Disconnect"
                >
                  <PhoneOff className="h-5 w-5" />
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full">
              <Headphones className="h-16 w-16 text-text-muted mb-4" />
              <h3 className="text-xl font-bold mb-2 text-text-primary">Voice Channel</h3>
              <p className="text-text-secondary text-center mb-6">
                Join the voice channel to chat with other members
              </p>
              <button
                className="btn btn-primary px-6 py-3 flex items-center"
                onClick={handleJoinVoice}
              >
                <Headphones className="h-5 w-5 mr-2" />
                Join Voice
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Stream viewer modal */}
      {watchingStream && (
        <VideoStreamViewer
          stream={watchingStream.stream}
          username={watchingStream.username}
          source={watchingStream.source}
          onClose={() => setWatchingStream(null)}
        />
      )}
    </div>
  );
};

export default VoiceChannel;